import React, { useEffect, useRef, useState } from "react";
import { TextField, Button } from "@mui/material";
import { ImagePlus, X, ArrowLeft, ArrowRight, Trash2 } from "lucide-react";
import { useAuth } from "../../services/AuthContext.js";
import {
  getPresignedUploadUrls,
  uploadImageToS3,
  updatePost,
} from "../../services/user_posts.js";
import "./create_post.css";

const MAX_IMAGES = 5;

function newLocalId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Edit an existing post (title, description, images).
 * Existing images keep their S3 keys; new files are uploaded via presign.
 */
function EditPostModal({ post, open, onClose, onUpdated }) {
  const { getIdToken } = useAuth();
  const inputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [images, setImages] = useState([]); // { id, url, key?, file? }
  const [dragIndex, setDragIndex] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !post) return;

    const keys = post.images || [];
    const urls = post.image_urls || [];
    setTitle(post.title || "");
    setDescription(post.description || "");
    setImages(
      keys.map((key, i) => ({
        id: `existing-${key}`,
        url: urls[i] || "",
        key,
      }))
    );
    setError("");
    setDragIndex(null);
  }, [open, post]);

  useEffect(() => {
    return () => {
      images.forEach((img) => {
        if (img.file && img.url?.startsWith("blob:")) URL.revokeObjectURL(img.url);
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!open || !post) return null;

  const openFileDialog = () => inputRef.current?.click();

  const addFiles = (fileList) => {
    const files = Array.from(fileList).filter((f) => f.type.startsWith("image/"));
    if (files.length === 0) return;

    setImages((prev) => {
      const remaining = MAX_IMAGES - prev.length;
      const toAdd = files.slice(0, remaining).map((f) => ({
        id: newLocalId(),
        url: URL.createObjectURL(f),
        file: f,
      }));
      return [...prev, ...toAdd];
    });
    setError("");
  };

  const handleInputChange = (e) => {
    if (e.target.files?.length) addFiles(e.target.files);
    e.target.value = "";
  };

  const removeImage = (id) => {
    setImages((prev) => {
      const img = prev.find((i) => i.id === id);
      if (img?.file && img.url?.startsWith("blob:")) URL.revokeObjectURL(img.url);
      return prev.filter((i) => i.id !== id);
    });
  };

  const moveImage = (from, to) => {
    setImages((prev) => {
      if (to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const onThumbDrop = (index) => {
    if (dragIndex === null || dragIndex === index) return;
    moveImage(dragIndex, index);
    setDragIndex(null);
  };

  const handleClose = () => {
    if (saving) return;
    images.forEach((img) => {
      if (img.file && img.url?.startsWith("blob:")) URL.revokeObjectURL(img.url);
    });
    onClose?.();
  };

  const canSave = title.trim() && description.trim() && images.length > 0 && !saving;

  const handleSave = async () => {
    if (!title.trim()) return setError("O título é obrigatório.");
    if (!description.trim()) return setError("A descrição é obrigatória.");
    if (images.length === 0) return setError("Adicione pelo menos uma imagem.");

    try {
      setSaving(true);
      setError("");
      const token = await getIdToken();

      const newImages = images.filter((img) => img.file);
      let uploadedById = {};

      if (newImages.length > 0) {
        const targets = await getPresignedUploadUrls(
          token,
          newImages.map((i) => ({ file_type: i.file.type }))
        );
        if (targets.length !== newImages.length) {
          throw new Error("Falha ao preparar o envio das imagens.");
        }
        await Promise.all(
          newImages.map((img, idx) => uploadImageToS3(targets[idx].upload_url, img.file))
        );
        uploadedById = Object.fromEntries(
          newImages.map((img, idx) => [img.id, targets[idx].image_key])
        );
      }

      const finalKeys = images.map((img) => {
        if (img.key) return img.key;
        const key = uploadedById[img.id];
        if (!key) throw new Error("Falha ao processar uma das imagens.");
        return key;
      });

      const updated = await updatePost(token, post.id, {
        title: title.trim(),
        description: description.trim(),
        images: finalKeys,
      });

      images.forEach((img) => {
        if (img.file && img.url?.startsWith("blob:")) URL.revokeObjectURL(img.url);
      });
      onUpdated?.(updated);
      onClose?.();
    } catch (err) {
      console.error("Erro ao atualizar publicação:", err);
      if (err.status === 403) {
        setError("Você não tem permissão para editar esta publicação.");
      } else if (err.status === 404) {
        setError("Publicação não encontrada.");
      } else {
        setError(err.message || "Não foi possível atualizar a publicação.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="create-post-overlay">
      <div className="create-post-modal" role="dialog" aria-modal="true" aria-label="Editar publicação">
        <div className="create-post-modal-header">
          <h3>Editar publicação</h3>
          <button
            type="button"
            className="create-post-close"
            onClick={handleClose}
            aria-label="Fechar"
            disabled={saving}
          >
            <X size={20} />
          </button>
        </div>

        <div className="create-post-modal-body">
          <span className="create-post-label">
            Imagens — arraste para reordenar ({images.length}/{MAX_IMAGES})
          </span>

          <div className="create-post-thumbs">
            {images.map((img, index) => (
              <div
                key={img.id}
                className={`create-post-thumb ${dragIndex === index ? "dragging" : ""}`}
                draggable
                onDragStartCapture={() => setDragIndex(index)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onThumbDrop(index)}
                onDragEnd={() => setDragIndex(null)}
              >
                <img src={img.url} alt={`Imagem ${index + 1}`} className="create-post-thumb-img" />
                <span className="create-post-thumb-order">{index + 1}</span>
                <div className="create-post-thumb-actions">
                  <button
                    type="button"
                    onClick={() => moveImage(index, index - 1)}
                    disabled={index === 0 || saving}
                    aria-label="Mover para a esquerda"
                  >
                    <ArrowLeft size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => removeImage(img.id)}
                    aria-label="Remover imagem"
                    className="create-post-thumb-remove"
                    disabled={saving}
                  >
                    <Trash2 size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => moveImage(index, index + 1)}
                    disabled={index === images.length - 1 || saving}
                    aria-label="Mover para a direita"
                  >
                    <ArrowRight size={14} />
                  </button>
                </div>
              </div>
            ))}

            {images.length < MAX_IMAGES && (
              <button
                type="button"
                className="create-post-add-more"
                onClick={openFileDialog}
                disabled={saving}
              >
                <ImagePlus size={22} />
                <span>Adicionar</span>
              </button>
            )}
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            multiple
            hidden
            onChange={handleInputChange}
          />

          <TextField
            label="Título"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
            size="small"
            margin="dense"
            required
            disabled={saving}
          />
          <TextField
            label="Descrição"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            minRows={3}
            size="small"
            margin="dense"
            required
            disabled={saving}
          />

          {error && <div className="create-post-error">{error}</div>}
        </div>

        <div className="create-post-modal-footer">
          <Button className="create-post-cancel-btn" onClick={handleClose} disabled={saving}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            className="create-post-save-btn"
            onClick={handleSave}
            disabled={!canSave}
          >
            {saving ? "Salvando..." : "Salvar alterações"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default EditPostModal;
